import React, { useEffect, useState } from "react";
import { Modal, Button, Spinner } from "react-bootstrap";
import { renderPdfToImages } from "../utils/pdfPrint";

// In-app document preview: PDFs render through the Google viewer with a download
// action, images render inline with zoom/rotate and a print action. Mirrors the
// homework file preview so both screens behave the same.

export const getFileType = (url) => {
    if (!url) return "";
    const cleanUrl = String(url).split("?")[0].toLowerCase();
    if (cleanUrl.endsWith(".pdf")) return "pdf";

    const docExtensions = [".docx", ".doc", ".xlsx", ".xls", ".pptx", ".ppt", ".zip", ".rar", ".txt", ".csv"];
    if (docExtensions.some((ext) => cleanUrl.endsWith(ext))) return "other";

    return "image";
};

const FilePreviewModal = ({ show, onHide, url, title, fallbackFileName = "document" }) => {
    const [zoomScale, setZoomScale] = useState(1);
    const [rotationDegrees, setRotationDegrees] = useState(0);
    const [fileLoading, setFileLoading] = useState(true);
    const [downloading, setDownloading] = useState(false);
    const [printing, setPrinting] = useState(false);

    useEffect(() => {
        if (!show) return;
        setZoomScale(1);
        setRotationDegrees(0);
        setFileLoading(true);
    }, [show, url]);

    const fileType = getFileType(url);

    const printFile = async () => {
        if (!url || printing) return;

        if (window.ReactNativeWebView) {
            setPrinting(true);
            window.ReactNativeWebView.postMessage(JSON.stringify({ type: "PRINT_FILE", url, filename: title }));
            setTimeout(() => setPrinting(false), 1500);
            return;
        }

        setPrinting(true);

        const isPdf = fileType === "pdf";

        const oldIframe = document.getElementById("filePreviewPrintIframe");
        if (oldIframe) oldIframe.remove();

        const iframe = document.createElement("iframe");
        iframe.id = "filePreviewPrintIframe";
        iframe.style.position = "fixed";
        iframe.style.right = "0";
        iframe.style.bottom = "0";
        iframe.style.width = "0";
        iframe.style.height = "0";
        iframe.style.border = "0";
        iframe.style.zIndex = "-9999";
        document.body.appendChild(iframe);

        try {
            let imageHtml = "";
            if (isPdf) {
                try {
                    const pageImages = await renderPdfToImages(url);
                    imageHtml = pageImages.map((src) => `<img src="${src}" class="pdfPrintPage" />`).join("");
                } catch (pdfErr) {
                    console.warn("PDF render for print failed:", pdfErr);
                    imageHtml = `<iframe src="${url}" style="width:100%;height:100vh;border:none;"></iframe>`;
                }
            } else {
                let printSrc = url;
                try {
                    const res = await fetch(url);
                    if (res.ok) {
                        const blob = await res.blob();
                        printSrc = window.URL.createObjectURL(blob);
                    }
                } catch (e) {
                    console.warn("Fetch blob failed for print image:", e);
                }
                imageHtml = `<img src="${printSrc}" class="pdfPrintPage" />`;
            }

            const docHtml = `
        <!DOCTYPE html>
        <html>
          <head>
            <title>${title || "Print Document"}</title>
            <meta name="viewport" content="width=device-width, initial-scale=1.0" />
            <style>
              body { margin: 0; padding: 0; background: #ffffff; text-align: center; }
              .pdfPrintPage { max-width: 100%; height: auto; display: block; margin: 0 auto 10px auto; page-break-after: always; }
              .pdfPrintPage:last-child { page-break-after: auto; }
              @media print {
                body { margin: 0; padding: 0; }
                .pdfPrintPage { max-width: 100%; width: 100%; height: auto; page-break-after: always; }
                .pdfPrintPage:last-child { page-break-after: auto; }
              }
            </style>
          </head>
          <body>
            ${imageHtml}
          </body>
        </html>
      `;

            const iframeDoc = iframe.contentWindow.document;
            iframeDoc.open();
            iframeDoc.write(docHtml);
            iframeDoc.close();

            setTimeout(() => {
                try {
                    iframe.contentWindow.focus();
                    iframe.contentWindow.print();
                } catch (e) {
                    console.error("Iframe print error:", e);
                }
                setPrinting(false);
                setTimeout(() => iframe.remove(), 2000);
            }, 500);
        } catch (err) {
            console.error("Print error:", err);
            iframe.remove();
            setPrinting(false);
        }
    };

    const downloadFile = async () => {
        if (!url || downloading) return;
        setDownloading(true);
        try {
            if (window.ReactNativeWebView) {
                window.ReactNativeWebView.postMessage(JSON.stringify({ type: "DOWNLOAD_FILE", url, filename: title }));
                setTimeout(() => setDownloading(false), 1500);
                return;
            }

            const cleanUrl = String(url).split("?")[0].toLowerCase();
            let ext = ".jpg";
            if (cleanUrl.endsWith(".pdf")) ext = ".pdf";
            else if (cleanUrl.endsWith(".png")) ext = ".png";
            else if (cleanUrl.endsWith(".jpg") || cleanUrl.endsWith(".jpeg")) ext = ".jpg";
            else if (cleanUrl.endsWith(".gif")) ext = ".gif";
            else if (cleanUrl.endsWith(".webp")) ext = ".webp";
            else if (cleanUrl.endsWith(".docx")) ext = ".docx";
            else if (cleanUrl.endsWith(".xlsx")) ext = ".xlsx";

            const name = (title || fallbackFileName).replace(/[^a-zA-Z0-9-_]/g, "_") + ext;

            // Method 1: fetch as blob
            try {
                const response = await fetch(url);
                if (response.ok) {
                    const blob = await response.blob();
                    const blobUrl = window.URL.createObjectURL(blob);
                    const link = document.createElement("a");
                    link.href = blobUrl;
                    link.download = name;
                    document.body.appendChild(link);
                    link.click();
                    setTimeout(() => {
                        document.body.removeChild(link);
                        window.URL.revokeObjectURL(blobUrl);
                    }, 1000);
                    setDownloading(false);
                    return;
                }
            } catch (fetchErr) {
                console.warn("Fetch blob download failed, trying canvas/direct download...", fetchErr);
            }

            // Method 2: canvas data url for images
            const isImage = [".png", ".jpg", ".jpeg", ".gif", ".webp"].some((e) => cleanUrl.endsWith(e));
            if (isImage) {
                try {
                    const dataUrl = await new Promise((resolve) => {
                        const img = new Image();
                        img.crossOrigin = "anonymous";
                        img.onload = () => {
                            try {
                                const canvas = document.createElement("canvas");
                                canvas.width = img.naturalWidth || img.width;
                                canvas.height = img.naturalHeight || img.height;
                                const ctx = canvas.getContext("2d");
                                ctx.drawImage(img, 0, 0);
                                resolve(canvas.toDataURL(ext === ".png" ? "image/png" : "image/jpeg", 0.95));
                            } catch (e) {
                                resolve(null);
                            }
                        };
                        img.onerror = () => resolve(null);
                        img.src = url;
                    });

                    if (dataUrl) {
                        const link = document.createElement("a");
                        link.href = dataUrl;
                        link.download = name;
                        document.body.appendChild(link);
                        link.click();
                        setTimeout(() => document.body.removeChild(link), 1000);
                        setDownloading(false);
                        return;
                    }
                } catch (canvasErr) {
                    console.warn("Canvas download failed", canvasErr);
                }
            }

            // Method 3: direct link click
            const link = document.createElement("a");
            link.href = url;
            link.download = name;
            document.body.appendChild(link);
            link.click();
            setTimeout(() => document.body.removeChild(link), 1000);
        } catch (error) {
            console.error("Error downloading file:", error);
        } finally {
            setDownloading(false);
        }
    };

    return (
        <Modal show={show} onHide={onHide} size="lg" centered className="file-preview-modal">
            <Modal.Header closeButton className="border-0 pb-0">
                <Modal.Title className="fw-bold fs-16 text-dark">{title || "Document"}</Modal.Title>
            </Modal.Header>
            <Modal.Body className="p-3">
                {fileType === "image" && (
                    <div className="d-flex justify-content-center gap-2 mb-3 bg-light p-2 rounded border border-light">
                        <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => setZoomScale((prev) => Math.max(0.2, prev - 0.1))}
                            title="Zoom Out"
                            className="d-flex align-items-center justify-content-center"
                            style={{ width: "32px", height: "32px", borderRadius: "6px" }}
                        >
                            <i className="ti ti-zoom-out fs-14" />
                        </Button>
                        <span
                            className="d-flex align-items-center justify-content-center fw-semibold text-secondary px-2"
                            style={{ fontSize: "13px", minWidth: "55px" }}
                        >
                            {Math.round(zoomScale * 100)}%
                        </span>
                        <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => setZoomScale((prev) => Math.min(3, prev + 0.1))}
                            title="Zoom In"
                            className="d-flex align-items-center justify-content-center"
                            style={{ width: "32px", height: "32px", borderRadius: "6px" }}
                        >
                            <i className="ti ti-zoom-in fs-14" />
                        </Button>
                        <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => setZoomScale(1)}
                            title="Reset Zoom"
                            className="d-flex align-items-center justify-content-center ms-2"
                            style={{ width: "32px", height: "32px", borderRadius: "6px" }}
                        >
                            <i className="ti ti-refresh fs-14" />
                        </Button>
                        <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => setRotationDegrees((prev) => (prev + 90) % 360)}
                            title="Rotate"
                            className="d-flex align-items-center justify-content-center ms-1"
                            style={{ width: "32px", height: "32px", borderRadius: "6px" }}
                        >
                            <i className="ti ti-rotate-clockwise fs-14" />
                        </Button>
                    </div>
                )}

                <div
                    className="d-flex align-items-center justify-content-center bg-light rounded position-relative"
                    style={{ minHeight: "450px", height: "60vh", overflow: "auto", border: "1px dashed #cbd5e1" }}
                >
                    {fileLoading && fileType !== "other" && (
                        <div className="position-absolute top-50 start-50 translate-middle text-center" style={{ zIndex: 15 }}>
                            <Spinner animation="border" variant="primary" />
                            <p className="text-muted small mt-2 mb-0 fw-medium">Loading document...</p>
                        </div>
                    )}

                    {fileType === "pdf" ? (
                        <div className="position-relative w-100 h-100 overflow-hidden">
                            <div
                                style={{
                                    position: "absolute",
                                    top: 0,
                                    right: 0,
                                    width: "48px",
                                    height: "48px",
                                    backgroundColor: "#ffffff",
                                    zIndex: 10,
                                    pointerEvents: "auto",
                                }}
                            />
                            <iframe
                                src={`https://docs.google.com/gview?url=${encodeURIComponent(url || "")}&embedded=true`}
                                className="w-100 h-100 rounded"
                                style={{ border: "none" }}
                                title="PDF Preview"
                                onLoad={() => setFileLoading(false)}
                            />
                        </div>
                    ) : fileType === "image" ? (
                        <div
                            className="d-flex align-items-center justify-content-center"
                            style={{ minWidth: "100%", minHeight: "100%", padding: "20px", overflow: "auto" }}
                        >
                            <img
                                src={url}
                                alt="Document Preview"
                                className="img-fluid rounded"
                                onLoad={() => setFileLoading(false)}
                                onError={() => setFileLoading(false)}
                                style={{
                                    maxHeight: "100%",
                                    transform: `scale(${zoomScale}) rotate(${rotationDegrees}deg)`,
                                    transformOrigin: "center center",
                                    transition: "transform 0.2s ease-in-out",
                                    objectFit: "contain",
                                }}
                            />
                        </div>
                    ) : (
                        <div className="text-center p-4">
                            <i className="ti ti-file-text text-muted mb-3" style={{ fontSize: "64px" }} />
                            <p className="text-secondary fw-medium mb-1">Preview not available for this file type</p>
                            <p className="text-muted small mb-0">Please download the file using the button below to view it.</p>
                        </div>
                    )}
                </div>
            </Modal.Body>
            <Modal.Footer className="border-0 pt-0">
                <Button variant="light" onClick={onHide} style={{ borderRadius: "8px", fontWeight: "600" }}>
                    Close
                </Button>
                {fileType === "image" ? (
                    <Button
                        variant="primary"
                        disabled={printing}
                        onClick={printFile}
                        className="d-flex align-items-center gap-2 px-4"
                        style={{ borderRadius: "8px", fontWeight: "600" }}
                    >
                        {printing ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-1" /> Preparing...
                            </>
                        ) : (
                            <>
                                <i className="ti ti-printer" /> Print
                            </>
                        )}
                    </Button>
                ) : (
                    <Button
                        variant="primary"
                        disabled={downloading}
                        onClick={downloadFile}
                        className="d-flex align-items-center gap-2 px-4"
                        style={{ borderRadius: "8px", fontWeight: "600" }}
                    >
                        {downloading ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-1" /> Downloading...
                            </>
                        ) : (
                            <>
                                <i className="ti ti-download" /> Download File
                            </>
                        )}
                    </Button>
                )}
            </Modal.Footer>
        </Modal>
    );
};

export default FilePreviewModal;
